import { isEmpty, isEqual, last } from "../../utils/lodash";
import { QuestionActionType } from "../../models/enums/QuestionActionType";
import {
    __isQuestionVisible__,
    __evaluateSafe__,
    __isBlockVisible__,
} from "../../expressions/parser";
import { ActionModel as QuestionAction } from "../../models/ActionModel";
import {
    branchTo,
    exitSurvey,
    setVariableValue,
    mobileAudioRecording,
    computeVariable,
    computeSelections,
    setloopRow,
    resumeLastSession,
} from "../../logic/questionLogic";
import { QuestionModel } from "../../models/QuestionModel";
import { Log } from "../../utils/logging";
import { DispositionCode } from "../../models/enums/DispositionCode";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { hasNextPage } from "../reducers/interview/navigationUtilities";
import { PageModel } from "../../models/PageModel";
import { QuestionType } from "../../models/enums/QuestionType";
import {
    shouldExecuteResumeActions,
    getCompletionQuestion,
    getQuestionWithName,
} from "../../models/utilities/questionnaireUtilities";
import { MentionModel } from "../../models/MentionModel";
import { MiddlewareType, StoreType, DispatchType } from "../actions/types";
import {
    navigationNextPage,
    navigationCompletionQuestionReached,
    navigationQuit,
    resetMentions,
    respondentSetEmail,
    respondentSetTimeZone,
    changeLanguage,
    respondentSetPhone,
    setVariableMentions,
    navigationPageReady,
    navigationReset,
    navigationPreviousPage,
    navigationDisplayMessage,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_SHORTCUT,
    NAVIGATION_BRANCH_TO,
    NAVIGATION_RESET,
    CONNECTION_CONNECTED,
    NAVIGATION_PAGE_READY,
    invalidateClientVariables,
    SERVER_PAGE_CHANGED,
} from "../actions";

const TAG = "flowController";

let mentionsBackup: { [variableName: string]: MentionModel[] };
let respondentInfoBackup: any;

const getCurrentPage = (store: StoreType): PageModel =>
    store.getState().interview.navigation.currentPage;

const getDisplayedQuestions = (store: StoreType) =>
    getCurrentPage(store).questions.filter((q) =>
        __isQuestionVisible__(q, store.getState().interview),
    );

const shouldExecuteAction = (store: StoreType, action: QuestionAction): boolean => {
    if (action.condition === undefined) return true;
    const result = __evaluateSafe__(store.getState().interview, action.condition).asBoolean();
    Log.d(
        TAG,
        "SHOULD_EXECUTE_ACTION",
        `${QuestionActionType[action.type]}:(${action.condition.expression})=${
            result ? "TRUE" : "FALSE"
        }`,
    );
    return result;
};

const nextPage = (store: StoreType, next: DispatchType) => {
    const prevNavState = store.getState().interview.navigation;
    // Navigate to the next page, keeping a reference of previous nav state.
    next(navigationNextPage());
    const navState = store.getState().interview.navigation;
    // Check if the completion question has been reached.
    if (!navState.completionQuestionReached) {
        const { interview } = store.getState();
        const completionQuestion = getCompletionQuestion(interview.questionnaire);
        // Check if the completion question exists.
        if (
            completionQuestion &&
            // Check if the current question is after the completion question.
            (navState.blockIndex > completionQuestion.blockIndex ||
                (navState.blockIndex === completionQuestion.blockIndex &&
                    navState.questionIndex >= completionQuestion.index)) &&
            // Check if the block of the completion question is skipped.
            __isBlockVisible__(
                navState.randomizedBlocks[completionQuestion.blockIndex],
                interview,
            ) &&
            // Check if the completion question is skipped.
            __isQuestionVisible__(completionQuestion, interview)
        ) {
            // The completion question has been reached.
            store.dispatch(navigationCompletionQuestionReached());
        }
    }
    // If we got the same page, we have reached the end of the survey.
    if (prevNavState === navState) {
        store.dispatch(navigationQuit(DispositionCode.Completed));
        return false;
    }
    return true;
};

const clearMentions = (store: StoreType, next: DispatchType) => {
    const { respondent, variables } = store.getState().interview;
    if (variables.mentionsByVariableName !== mentionsBackup) {
        next(resetMentions(mentionsBackup));
    }

    if (respondentInfoBackup) {
        if (respondent.email !== respondentInfoBackup.email)
            next(respondentSetEmail(respondentInfoBackup.email));
        if (respondent.timeZone !== respondentInfoBackup.timeZone)
            next(respondentSetTimeZone(respondentInfoBackup.timeZone));
        if (respondent.language !== respondentInfoBackup.language)
            next(changeLanguage(respondentInfoBackup.language));
        if (respondent.phone !== respondentInfoBackup.phone)
            next(respondentSetPhone(respondentInfoBackup.phone));
    }
};

/**
 * Finds the question the interview should begin at, which is either RESUME_COMPLETED_QUESTION,
 * RESUME_NOT_COMPLETED_QUESTION, the survey's START_QUESTION, the server current question, the last answered question or undefined.
 */
const getStartQuestion = (store: StoreType) => {
    const { interview } = store.getState();
    const { respondent, questionnaire } = interview;

    // Create a helper method to find a question by name and return it.
    let question: QuestionModel = null;
    const findQuestion = (name: string) => (question = getQuestionWithName(questionnaire, name));
    // If the respondent is resumed.
    if (!respondent.isNew) {
        // If the respondent is completed, find RESUME_COMPLETED_QUESTION.
        if (
            respondent.dispositionCode === DispositionCode.Completed &&
            findQuestion(getSettingString(questionnaire, "RESUME_COMPLETED_QUESTION"))
        )
            return question;
        // If the respondent is completed, find RESUME_NOT_COMPLETED_QUESTION.
        if (
            respondent.dispositionCode !== DispositionCode.Completed &&
            findQuestion(getSettingString(questionnaire, "RESUME_NOT_COMPLETED_QUESTION"))
        )
            return question;
    }
    // If there is a start question.
    if (findQuestion(getSettingString(questionnaire, "START_QUESTION"))) return question;
    // If all else fails, return the last answered question or undefined if it does not exist.
    return findQuestion(respondent.prevSessionLastAnsweredQuestion);
};

/**
 * Returns true if the respondent is resumed to the last answered question or to the START_QUESTION,
 * RESUME_COMPLETED_QUESTION or RESUME_NOT_COMPLETED_QUESTION.
 */
const isRespondentResumed = (store: StoreType) => {
    const respondent = store.getState().interview.respondent;
    const questionnaire = store.getState().interview.questionnaire;
    // Create a helper method to find a question by name and return if it exists.
    const questionExists = (name: string) => getQuestionWithName(questionnaire, name) != null;
    // If the respondent is resumed.
    if (!respondent.isNew) {
        // If the respondent is completed, find RESUME_COMPLETED_QUESTION.
        if (
            respondent.dispositionCode === DispositionCode.Completed &&
            questionExists(getSettingString(questionnaire, "RESUME_COMPLETED_QUESTION"))
        )
            return false;
        // If the respondent is completed, find RESUME_NOT_COMPLETED_QUESTION.
        if (
            respondent.dispositionCode !== DispositionCode.Completed &&
            questionExists(getSettingString(questionnaire, "RESUME_NOT_COMPLETED_QUESTION"))
        )
            return false;
    }
    // If there is a start question.
    if (questionExists(getSettingString(questionnaire, "START_QUESTION"))) return false;
    // If all else fails, return true.
    return true;
};

/**
 * Dispatch a single action from a question.
 * Returns true if navigation should be interrupted.
 */
const dispatchAction = (
    store: StoreType,
    next: DispatchType,
    question: QuestionModel,
    action: QuestionAction,
) => {
    const transformAction: any = {
        [QuestionActionType.BranchTo]: () => branchTo(store, question, action),
        [QuestionActionType.ExitSurvey]: () => exitSurvey(store, action),
        [QuestionActionType.SetVariableValue]: () => setVariableValue(store, action),
        [QuestionActionType.MobileAudioRecording]: () => mobileAudioRecording(store, action),
        [QuestionActionType.ComputeVariable]: () => computeVariable(store, action),
        [QuestionActionType.Selection]: () => computeSelections(store, action),
        [QuestionActionType.SetLoopRow]: () => setloopRow(store, action),
        [QuestionActionType.ResumeLastSession]: () => resumeLastSession(store, question, action),
        [QuestionActionType.ExecuteSQL]: () => store.dispatch(invalidateClientVariables()),
    };
    if (transformAction[action.type] != null) return transformAction[action.type]();
};

/**
 * Dispatch actions from a question.
 *
 * Returns true when an action is preventing future actions from executing.
 * e.g. a branching action.
 */
const dispatchQuestionActions = (
    store: StoreType,
    next: DispatchType,
    question: QuestionModel,
    questionActions: QuestionAction[],
    skipAdvancedActions?: boolean,
) => {
    for (const action of questionActions || []) {
        // Skip advanced actions.
        if (skipAdvancedActions && action.type !== QuestionActionType.BranchTo) continue;
        // Do not execute the action if the logic returns false.
        if (!shouldExecuteAction(store, action)) continue;
        // Dispatch the action.
        if (dispatchAction(store, next, question, action)) return true; // Return true to prevent future actions from executing.
    }
};

/**
 * Executes post answer actions for all displayed questions.
 *
 * Returns false if an action is preventing future actions from executing.
 * e.g. a branching action.
 */
const executePostAnswerActions = (
    store: StoreType,
    next: DispatchType,
    skipAdvancedActions?: boolean,
) => {
    for (const question of getDisplayedQuestions(store)) {
        if (
            dispatchQuestionActions(
                store,
                next,
                question,
                question.postAnswerActions,
                skipAdvancedActions,
            )
        )
            return true;
    }
};

/**
 * Executes side-effects from questions in the page.
 *
 * Returns false if an action is preventing future actions from executing.
 * e.g. a branching action.
 */
const executeSideEffects = (store: StoreType, next: DispatchType) => {
    const metaData = getDisplayedQuestions(store).find(
        (question) => question.type === QuestionType.RespondentMetaData,
    );

    if (metaData) {
        const UAParser = require("ua-parser-js");
        const ua = new UAParser().getResult();
        const browserLanguageVar = getSettingString(metaData, "BrowserLanguageVariableName");
        const ipAddressVar = getSettingString(metaData, "IpAddressVariableName");
        const javaSupportVar = getSettingString(metaData, "JavaSupportVariableName");
        const operatingSystemVar = getSettingString(metaData, "OperatingSystemVariableName");
        const refererVar = getSettingString(metaData, "RefererVariableName");
        const userAgentVar = getSettingString(metaData, "UserAgentVariableName");
        const { ipAddress } = store.getState().interview.respondent;
        if (!isEmpty(browserLanguageVar) && typeof window !== "undefined")
            next(
                setVariableMentions(browserLanguageVar, (m) => m.set(window?.navigator?.language)),
            );
        if (!isEmpty(ipAddressVar))
            next(setVariableMentions(ipAddressVar, (m) => m.set(ipAddress)));
        if (!isEmpty(javaSupportVar) && typeof window !== "undefined") {
            next(
                setVariableMentions(javaSupportVar, (m) =>
                    m.set(window.navigator.javaEnabled() ? "True" : "False"),
                ),
            );
        }
        if (!isEmpty(operatingSystemVar)) {
            next(
                setVariableMentions(operatingSystemVar, (m) =>
                    m.set(`${ua.os.name} ${ua.os.version}`),
                ),
            );
        }
        if (!isEmpty(refererVar) && typeof window !== "undefined")
            next(setVariableMentions(refererVar, (m) => m.set(window.location.href)));
        if (!isEmpty(userAgentVar)) {
            next(
                setVariableMentions(userAgentVar, (m) =>
                    m.set(`${ua.browser.name} ${ua.browser.version}`),
                ),
            );
        }
        if (
            !getDisplayedQuestions(store).some(
                (question) => question.type !== QuestionType.RespondentMetaData,
            )
        ) {
            nextPage(store, next);
            return false;
        }
    }

    return true;
};

/**
 * Executes pre-load answer actions for all displayed questions.
 *
 * Returns false if an action is preventing future actions from executing.
 * e.g. a branching action.
 */
const executePreLoadActions = (
    store: StoreType,
    next: DispatchType,
    skipAdvancedActions?: boolean,
) => {
    for (const question of getDisplayedQuestions(store)) {
        if (
            dispatchQuestionActions(
                store,
                next,
                question,
                question.preLoadActions,
                skipAdvancedActions,
            )
        )
            return true;
    }
};

/**
 * Validates pages in a loop making sure they don't appear twice.
 */
const createInfiniteLoopValidator = () => {
    const stack = [];

    function pageQuestionIndices(page: PageModel) {
        return page.questions.map((q) => q.index);
    }

    function stackContainsPage(page: PageModel) {
        const questionIndices = pageQuestionIndices(page);
        return stack.some((p) => {
            if (p.blockIndex !== page.blockIndex || p.loopIndex !== page.loopIndex) return false;
            return isEqual(p.questionIndices, questionIndices);
        });
    }

    function validate(page: PageModel) {
        if (stackContainsPage(page)) return false;
        const { blockIndex, loopIndex } = page;
        stack.push({ blockIndex, loopIndex, questionIndices: pageQuestionIndices(page) });
        return true;
    }

    return validate;
};

/**
 * Evaluates preload actions and skips all empty pages until it lands on a valid page.
 */
const navigateToFirstValidPage = (
    store: StoreType,
    next: DispatchType,
    skipAdvancedActions?: boolean,
) => {
    // Loop indefinitely.
    const loopValidator = createInfiniteLoopValidator();
    while (true) {
        const currentPage = getCurrentPage(store);

        // If the page is already valid, return.
        if (currentPage.isReady) {
            return;
        }

        // Make sure we don't go in an infinite loop by checking the stack.
        if (!loopValidator(currentPage)) {
            Log.e(TAG, "INFINITE_LOOP_DETECTED", `An infinite loop was detected.`);
            break;
        }

        // Execute question side-effects (ex. Respondent Metadata).
        if (!executeSideEffects(store, next)) {
            // A branching action changed the current page.
            continue;
        }

        // If the page has no visible questions, go to the next page.
        if (getDisplayedQuestions(store).length === 0) {
            Log.d(TAG, "PAGE_EMPTY", "Page has no visible questions, navigating to the next page.");
            if (!nextPage(store, next)) {
                return;
            }
            continue;
        }

        // Execute preload actions for the current page.
        if (executePreLoadActions(store, next, skipAdvancedActions)) {
            // A branching action changed the current page.
            if (getCurrentPage(store) !== currentPage) continue;
        }

        // The page is valid, notify the store that the page is ready to be rendered and break.
        store.dispatch(navigationPageReady());
        break;
    }
};

/**
 * Navigate to the start question, executing question actions along the way.
 */
const navigateToStartQuestion = (store: StoreType, next: DispatchType) => {
    // Get the start question.
    const startQuestion = getStartQuestion(store);
    const { questionnaire } = store.getState().interview;
    const skipAdvancedActions = !shouldExecuteResumeActions(questionnaire);

    // If there is no start question, just return the first page.
    if (!startQuestion) {
        next(navigationReset());
        navigateToFirstValidPage(store, next);
        return;
    }

    Log.i(TAG, "NAVIGATE_TO_START_QUESTION", startQuestion.name);
    if (skipAdvancedActions) {
        Log.i(TAG, "NAVIGATE_TO_START_QUESTION", `Skipping advanced actions.`);
    }

    // Find the start question matrix from restored state.
    const startBlock = store.getState().interview.questionnaire.blocks[startQuestion.blockIndex];
    const startMatrix =
        store.getState().interview.navigation.loopByBlockName[startBlock.name.toUpperCase()] || 1;

    // Reset navigation to the start of the survey.
    next(navigationReset());

    // Try to navigate to the start question.
    const loopValidator = createInfiniteLoopValidator();

    while (true) {
        const currentPage = getCurrentPage(store);

        // Make sure we don't go in an infinite loop by checking the stack.
        if (!loopValidator(currentPage)) {
            Log.e(
                TAG,
                "INFINITE_LOOP_DETECTED",
                `An infinite loop was detected navigating to start question "${startQuestion.name}".`,
            );
            break;
        }

        // Check if the page has some questions.
        const displayedQuestions = getDisplayedQuestions(store);
        if (displayedQuestions.length === 0) {
            Log.d(TAG, "PAGE_EMPTY", "Page has no visible questions, navigating to the next page.");
            // Go to the next page and restart the loop.
            if (!nextPage(store, next)) {
                return;
            }
            continue;
        }

        // Check if the current page contains the question or if we passed it already.
        const lastQuestion = last(displayedQuestions);
        const didPassLoop = currentPage.loopIndex >= startMatrix;
        const isCurrentBlock = currentPage.blockIndex === startQuestion.blockIndex && didPassLoop;
        const didPassBlock = currentPage.blockIndex > startQuestion.blockIndex;
        const didPassQuestion =
            didPassBlock || (lastQuestion.index >= startQuestion.index && isCurrentBlock);

        if (didPassQuestion) {
            // If we were navigating to the last answered question and there is a next page, go to the next page.
            // Otherwise the page contains the start question so we should not skip it.
            if (
                isRespondentResumed(store) &&
                hasNextPage(store.getState().interview.navigation) &&
                lastQuestion.index === startQuestion.index
            ) {
                // Notify the page is ready so it gets saved in history.
                store.dispatch(navigationPageReady());

                if (getCurrentPage(store).previewQuestion) {
                    return;
                }

                // Execute post-answer actions for this page.
                if (executePostAnswerActions(store, next, skipAdvancedActions)) {
                    // An action resulted in a page change, restart the loop.
                    continue;
                }

                // Navigate to the next page.
                if (!nextPage(store, next)) {
                    return;
                }

                // If the page is empty, go back.
                if (getCurrentPage(store).questions.length === 0) {
                    next(navigationPreviousPage());
                }
            }

            // We have found the start page; return.
            navigateToFirstValidPage(store, next, skipAdvancedActions);
            return;
        }

        // Execute pre-load actions in the current page.
        if (executePreLoadActions(store, next, skipAdvancedActions)) {
            // An action resulted in a page change, restart the loop.
            continue;
        }

        // Notify the page is ready so it gets saved in history.
        store.dispatch(navigationPageReady());

        if (getCurrentPage(store).previewQuestion) {
            return;
        }

        // Execute post-answer actions in the current page.
        if (executePostAnswerActions(store, next, skipAdvancedActions)) {
            // An action resulted in a page change, restart the loop.
            continue;
        }

        // If we have reached the end of the survey, break the loop.
        if (!hasNextPage(store.getState().interview.navigation)) {
            break;
        }

        // Go to the next page and restart the loop.
        if (!nextPage(store, next)) {
            return;
        }
    }

    Log.w(TAG, "NAVIGATE_TO_START_QUESTION", `Start question "${startQuestion.name}" not found.`);

    // Reset navigation and display the first page.
    next(navigationReset());
    navigateToFirstValidPage(store, next, skipAdvancedActions);
};

export const pageReady = (store: StoreType) => {
    const { respondent, variables } = store.getState().interview;
    mentionsBackup = variables.mentionsByVariableName;
    respondentInfoBackup = {
        email: respondent.email,
        language: respondent.language,
        timeZone: respondent.timeZone,
        phone: respondent.phone,
    };
};

/**
 * Execute preload and post-answer actions on page changes.
 */
export const flowController: MiddlewareType = (store) => (next) => (action) => {
    // Catch all errors regarding navigation and crash the survey.
    try {
        // If the current page on surver is unknown, use this flowController
        if (!store.getState().interview.navigation?.currentPageOnServer) {
            switch (action.type) {
                case NAVIGATION_NEXT_PAGE: {
                    // If the current page was rendered, execute post answer actions before changing page.
                    if (getCurrentPage(store).isReady && executePostAnswerActions(store, next))
                        return action;
                    if (nextPage(store, next)) navigateToFirstValidPage(store, next);
                    return action;
                }
                case NAVIGATION_PREVIOUS_PAGE: {
                    clearMentions(store, next);
                    const result = next(action);
                    // Page is ready to render.
                    store.dispatch(navigationPageReady());
                    return result;
                }
                case NAVIGATION_SHORTCUT: {
                    const result = next(action);
                    // Shortcuts do not evaluate block skip logic.
                    // But they do execute pre-load actions.
                    if (executePreLoadActions(store, next))
                        return navigateToFirstValidPage(store, next);
                    // Page is ready to render.
                    store.dispatch(navigationPageReady());
                    return result;
                }
                case NAVIGATION_BRANCH_TO:
                case NAVIGATION_RESET: {
                    const result = next(action);
                    navigateToFirstValidPage(store, next);
                    return result;
                }
                case CONNECTION_CONNECTED: {
                    const result = next(action);
                    navigateToStartQuestion(store, next);
                    return result;
                }
                case SERVER_PAGE_CHANGED:
                case NAVIGATION_PAGE_READY: {
                    pageReady(store);
                    break;
                }
                default:
                    break;
            }
        } else {
            switch (action.type) {
                case NAVIGATION_NEXT_PAGE:
                    pageReady(store);
                    break;
                case NAVIGATION_PREVIOUS_PAGE:
                    clearMentions(store, next);
                    break;
                default:
                    break;
            }
        }
    } catch (ex) {
        store.dispatch(navigationDisplayMessage(ex));
        throw ex;
    }

    return next(action);
};
